import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { App, Checkbox, Col, Form, Row, Tooltip } from "antd";
import { useFormService, useI18n } from "../../../../../contexts";
import { EServiceTemplateGoogleConsentModeTypes } from "@devowl-wp/api-real-cookie-banner";
import { QuestionCircleFilled } from "@ant-design/icons";
import { copyToClipboard, slugify } from "@devowl-wp/react-utils";
import { FormValueDifferFromTemplateTag, diffByJsonStringify } from "../../..";
import { getManagerDependingVariables } from "@devowl-wp/cookie-consent-web-client";
const FormServiceFieldHandlingGoogleConsentMode = ()=>{
    const { __, _i } = useI18n();
    const { isGcm, setCookiesViaManager, template } = useFormService();
    const [ref, setRef] = useState();
    const { message } = App.useApp();
    const { useManager, serviceIsManager } = getManagerDependingVariables(setCookiesViaManager, {
        isGcm,
        presetId: template == null ? void 0 : template.identifier
    });
    const googleContentModeTranslations = useMemo(()=>({
            [EServiceTemplateGoogleConsentModeTypes.AdStorage]: __("Storing and reading of data such as cookies (web) or device identifiers (apps) related to advertising."),
            [EServiceTemplateGoogleConsentModeTypes.AdUserData]: __("Sending user data to Google for online advertising purposes."),
            [EServiceTemplateGoogleConsentModeTypes.AdPersonalization]: __("Evaluation and display of personalized advertising."),
            [EServiceTemplateGoogleConsentModeTypes.AnalyticsStorage]: __("Storing and reading of data such as cookies (web) or device identifiers (apps), related to analytics (e.g. visit duration)."),
            [EServiceTemplateGoogleConsentModeTypes.FunctionalityStorage]: __("Storing and reading of data that supports the functionality of the website or app (e.g. language settings)."),
            [EServiceTemplateGoogleConsentModeTypes.PersonalizationStorage]: __("Storing and reading of data related to personalization (e.g. video recommendations)."),
            [EServiceTemplateGoogleConsentModeTypes.SecurityStorage]: __("Storing and reading of data related to security (e.g. authentication functionality, fraud prevention, and other user protection).")
        }), []);
    const uniqueNameAdditionalConsentTypeComponent = useManager === "googleTagManagerWithGcm" && /*#__PURE__*/ _jsx(Col, {
        span: 24,
        children: /*#__PURE__*/ _jsx(Form.Item, {
            noStyle: true,
            shouldUpdate: (param, param1)=>{
                let { uniqueName: aUniqueName, name: aName } = param;
                let { uniqueName: bUniqueName, name: bName } = param1;
                return aUniqueName !== bUniqueName || aName !== bName;
            },
            children: (param)=>{
                let { getFieldValue } = param;
                const uniqueName = getFieldValue("uniqueName") || slugify(getFieldValue("name"));
                return !!uniqueName && /*#__PURE__*/ _jsx(Tooltip, {
                    title: __('Accessible as "Additional Consent" in Google Tag Manager to fire tags only if consent for this service has been given.'),
                    children: /*#__PURE__*/ _jsxs(Checkbox, {
                        checked: true,
                        disabled: true,
                        children: [
                            /*#__PURE__*/ _jsx("code", {
                                onClick: ()=>{
                                    copyToClipboard(uniqueName);
                                    message.success(__("Successfully copied to the clipboard!"));
                                },
                                children: uniqueName
                            }),
                            ": ",
                            __("Unique identifier of this service"),
                            "\xa0",
                            /*#__PURE__*/ _jsx(QuestionCircleFilled, {})
                        ]
                    })
                });
            }
        })
    });
    return isGcm && !serviceIsManager && /*#__PURE__*/ _jsxs(Form.Item, {
        label: __("Requested consent types for Google Consent Mode"),
        children: [
            /*#__PURE__*/ _jsxs(Row, {
                children: [
                    uniqueNameAdditionalConsentTypeComponent,
                    /*#__PURE__*/ _jsx(Form.Item, {
                        noStyle: true,
                        name: "googleConsentModeConsentTypes",
                        children: /*#__PURE__*/ _jsx(Checkbox.Group, {
                            style: {
                                marginTop: 6
                            },
                            children: Object.values(EServiceTemplateGoogleConsentModeTypes).map((val)=>/*#__PURE__*/ _jsx(Col, {
                                    span: 24,
                                    children: /*#__PURE__*/ _jsxs(Checkbox, {
                                        value: val,
                                        children: [
                                            /*#__PURE__*/ _jsx("code", {
                                                children: val
                                            }),
                                            ": ",
                                            googleContentModeTranslations[val]
                                        ]
                                    })
                                }, val))
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "description",
                style: {
                    marginTop: 10
                },
                ref: setRef,
                children: [
                    /*#__PURE__*/ _jsx(FormValueDifferFromTemplateTag, {
                        form: "service",
                        valueName: "googleConsentModeConsentTypes",
                        widthOfRef: ref,
                        difference: diffByJsonStringify,
                        renderDiff: (value)=>/*#__PURE__*/ _jsxs(Row, {
                                children: [
                                    uniqueNameAdditionalConsentTypeComponent,
                                    /*#__PURE__*/ _jsx(Checkbox.Group, {
                                        style: {
                                            marginTop: 6
                                        },
                                        value: value,
                                        disabled: true,
                                        children: Object.values(EServiceTemplateGoogleConsentModeTypes).map((val)=>/*#__PURE__*/ _jsx(Col, {
                                                span: 24,
                                                children: /*#__PURE__*/ _jsxs(Checkbox, {
                                                    value: val,
                                                    children: [
                                                        /*#__PURE__*/ _jsx("code", {
                                                            children: val
                                                        }),
                                                        ": ",
                                                        googleContentModeTranslations[val]
                                                    ]
                                                })
                                            }, val))
                                    })
                                ]
                            })
                    }),
                    _i(__("You must obtain consent according to the Google Consent Mode based on so-called consent types, {{strong}}if the service can respect these consent types{{/strong}} (check the documentation of the provider of this service to find out if an integration has been implemented). Especially for Google services, you should request all necessary consent types in order to be able to collect as much data as possible. Consent in consent types is only granted if the website visitor explicitly agrees to all services (with all features) or explicitly agrees to these consent types in the individual privacy settings, but not if they do not exercise their right to object if you use this service on the legal basis of a legitimate interest."), {
                        strong: /*#__PURE__*/ _jsx("strong", {})
                    })
                ]
            })
        ]
    });
};
export { FormServiceFieldHandlingGoogleConsentMode };
